type CalendarDateInput =
  | Date
  | string
  | { toDate?: () => Date; seconds?: number; nanoseconds?: number }
  | null
  | undefined;

function isUtcDateOnly(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

function calendarPartsFromDate(date: Date): {
  year: number;
  month: number;
  day: number;
} {
  if (isUtcDateOnly(date)) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

/** Parse API / form values as a calendar date stored at UTC noon (no day shift). */
export function parseCalendarDate(value: CalendarDateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    const { year, month, day } = calendarPartsFromDate(value);
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
  }

  if (typeof value === "string") {
    const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnly) {
      const [year, month, day] = dateOnly[1].split("-").map(Number);
      return new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parseCalendarDate(parsed);
  }

  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      try {
        const converted = value.toDate();
        return converted instanceof Date ? parseCalendarDate(converted) : null;
      } catch (error) {
        console.warn("Failed to convert Firestore timestamp:", error);
        return null;
      }
    }
    if (typeof value.seconds === "number") {
      return parseCalendarDate(
        new Date(
          value.seconds * 1000 +
            (value.nanoseconds ? Math.floor(value.nanoseconds / 1e6) : 0),
        ),
      );
    }
  }

  return null;
}

/** Format a calendar date for display without UTC/local day drift. */
export function formatCalendarDate(
  value: CalendarDateInput,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = parseCalendarDate(value);
  if (!date) return "—";
  return date.toLocaleDateString(locale, {
    timeZone: "UTC",
    ...options,
  });
}

/** Format a date for HTML `<input type="date">` (YYYY-MM-DD). */
export function toDateInputValue(value: CalendarDateInput): string {
  const date = parseCalendarDate(value);
  if (!date) return "";

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Utility to safely handle optional Date values and convert various date formats
export const safeDate = (d?: Date | unknown): Date => {
  const parsed = parseCalendarDate(d as CalendarDateInput);
  return parsed ?? new Date();
};

// Utility to get a far future date for calculations (current year + years ahead)
export const getFarFutureDate = (yearsAhead: number = 20): Date => {
  const currentYear = new Date().getFullYear();
  return new Date(currentYear + yearsAhead, 11, 1); // December 1st of far future year
};
